import { cardLabel, cardName, isRed, RANK_NAMES, RANKS, SUIT_NAMES, SUITS, } from '../cards.js';
import { IllegalMoveError, } from './types.js';
/** Ace low, King high — the order foundations build in. */
export function rankIndex(card) {
    return RANKS.indexOf(card.rank);
}
const ACE = 0;
const KING = RANKS.length - 1;
function alternatingColours(a, b) {
    return isRed(a.suit) !== isRed(b.suit);
}
/** Tableau builds down in alternating colours: a red 9 takes a black 8. */
export function canStack(moving, onto) {
    return alternatingColours(moving, onto) && rankIndex(moving) === rankIndex(onto) - 1;
}
/** Only a King opens an empty column, unless the house rule says otherwise. */
export function canFillEmptyColumn(moving, rules) {
    return rules.anyCardFillsEmptyColumn || rankIndex(moving) === KING;
}
/** Foundations build up from the Ace, one suit each. */
export function canPlaceOnFoundation(card, foundation) {
    const top = foundation[foundation.length - 1];
    if (!top)
        return rankIndex(card) === ACE;
    return card.suit === top.suit && rankIndex(card) === rankIndex(top) + 1;
}
/**
 * Is this face-up slice a legal run to carry as a unit?
 *
 * A well-formed state always keeps `up` as one descending alternating run, so
 * this is really a guard against a corrupt or hand-built state rather than a
 * check the UI is expected to fail.
 */
export function isValidRun(cards) {
    for (let i = 1; i < cards.length; i++) {
        if (!canStack(cards[i], cards[i - 1]))
            return false;
    }
    return true;
}
function requireColumn(state, index) {
    const pile = state.tableau[index];
    if (!pile)
        throw new IllegalMoveError('That column does not exist.');
    return pile;
}
/**
 * The cards a source refers to, top of the carried run first.
 *
 * Throws rather than returning null, matching `validatePlay` on the Switch
 * side — callers are already inside a try/catch that turns this into a
 * protocol error.
 */
export function resolveSource(state, from) {
    switch (from.zone) {
        case 'waste': {
            const card = state.waste[state.waste.length - 1];
            if (!card)
                throw new IllegalMoveError('There is nothing turned up to take.');
            return [card];
        }
        case 'foundation': {
            if (!state.rules.allowFoundationToTableau) {
                throw new IllegalMoveError('Cards cannot come back off the foundations.');
            }
            const pile = state.foundations[from.suit];
            const card = pile[pile.length - 1];
            if (!card)
                throw new IllegalMoveError('That foundation is empty.');
            return [card];
        }
        case 'tableau': {
            const pile = requireColumn(state, from.index);
            if (from.depth < 1 || from.depth > pile.up.length) {
                throw new IllegalMoveError('There are not that many cards face up in that column.');
            }
            const run = pile.up.slice(pile.up.length - from.depth);
            if (!isValidRun(run))
                throw new IllegalMoveError('Those cards are not in sequence.');
            return run;
        }
    }
}
/** Can `cards` (a validated run) legally land on `to`? */
export function canPlace(state, cards, to) {
    const lead = cards[0];
    if (!lead)
        return false;
    if (to.zone === 'foundation') {
        // Foundations take one card at a time, and only of their own suit.
        if (cards.length !== 1 || lead.suit !== to.suit)
            return false;
        return canPlaceOnFoundation(lead, state.foundations[to.suit]);
    }
    const pile = state.tableau[to.index];
    if (!pile)
        return false;
    const onto = pile.up[pile.up.length - 1];
    if (!onto)
        return pile.down.length === 0 && canFillEmptyColumn(lead, state.rules);
    return canStack(lead, onto);
}
/** True when a move would put a card back exactly where it came from. */
function isNoop(from, to) {
    return from.zone === 'tableau' && to.zone === 'tableau' && from.index === to.index;
}
export function validateMove(state, from, to) {
    if (state.phase !== 'playing')
        throw new IllegalMoveError('The game is over.');
    if (isNoop(from, to))
        throw new IllegalMoveError('That card is already there.');
    const cards = resolveSource(state, from);
    if (!canPlace(state, cards, to))
        throw new IllegalMoveError('That card cannot go there.');
    return cards;
}
// ---------------------------------------------------------------------------
// Enumeration
// ---------------------------------------------------------------------------
function pushIfLegal(state, moves, from, to, cards) {
    if (isNoop(from, to))
        return;
    if (canPlace(state, cards, to))
        moves.push({ kind: 'move', from, to, cards: cards.slice() });
}
/**
 * Every move available right now.
 *
 * Deliberately exhaustive rather than clever: the board is tiny, and the client
 * uses this both to light up draggable cards and to decide whether the deal is
 * dead. Foundation moves are listed before tableau ones so a UI that takes the
 * first match on a double-click banks the card.
 */
export function legalMoves(state) {
    const moves = [];
    if (state.phase !== 'playing')
        return moves;
    const sources = [];
    const wasteTop = state.waste[state.waste.length - 1];
    if (wasteTop)
        sources.push({ from: { zone: 'waste' }, cards: [wasteTop] });
    state.tableau.forEach((pile, index) => {
        // Every depth is a candidate: the whole run can move, and so can any
        // sub-run hanging off the bottom of it.
        for (let depth = 1; depth <= pile.up.length; depth++) {
            const run = pile.up.slice(pile.up.length - depth);
            if (!isValidRun(run))
                break;
            sources.push({ from: { zone: 'tableau', index, depth }, cards: run });
        }
    });
    if (state.rules.allowFoundationToTableau) {
        for (const suit of SUITS) {
            const top = state.foundations[suit][state.foundations[suit].length - 1];
            if (top)
                sources.push({ from: { zone: 'foundation', suit }, cards: [top] });
        }
    }
    for (const { from, cards } of sources) {
        const lead = cards[0];
        if (cards.length === 1) {
            pushIfLegal(state, moves, from, { zone: 'foundation', suit: lead.suit }, cards);
        }
        for (let index = 0; index < state.tableau.length; index++) {
            pushIfLegal(state, moves, from, { zone: 'tableau', index }, cards);
        }
    }
    if (state.stock.length > 0) {
        moves.push({ kind: 'draw', count: Math.min(state.rules.drawCount, state.stock.length) });
    }
    else if (state.waste.length > 0 && canRecycle(state)) {
        moves.push({ kind: 'recycle', count: state.waste.length });
    }
    return moves;
}
export function canRecycle(state) {
    if (state.waste.length === 0)
        return false;
    return state.rules.redeals === null || state.redealsUsed < state.rules.redeals;
}
/** Every card is on a foundation. */
export function isWon(state) {
    return SUITS.every((suit) => state.foundations[suit].length === RANKS.length);
}
/**
 * Is the deal genuinely dead?
 *
 * A move that only shuffles the stock along does not count as progress, so
 * "there is still a draw available" is not enough to call a game live. Instead
 * we ask whether any card that could yet surface has somewhere to go.
 *
 * The stock check is an over-approximation: when redeals remain we treat every
 * buried card as reachable, which is exactly true at draw-one but slightly
 * optimistic at draw-three, where a pass can bury a card behind the same two
 * neighbours forever. Erring that way is deliberate — declaring a live game
 * dead is a far worse bug than letting a doomed one run on.
 */
export function isDeadlocked(state) {
    if (state.phase !== 'playing')
        return false;
    if (isWon(state))
        return false;
    if (legalMoves(state).some((m) => m.kind === 'move'))
        return false;
    if (state.stock.length === 0 && !canRecycle(state))
        return true;
    // Nothing on the board moves. Could anything still buried in the stock or
    // waste land somewhere once it surfaces?
    const reachable = state.stock.length > 0 || canRecycle(state)
        ? [...state.stock, ...state.waste]
        : state.waste.slice(-1);
    for (const card of reachable) {
        if (canPlaceOnFoundation(card, state.foundations[card.suit]))
            return false;
        for (const pile of state.tableau) {
            const onto = pile.up[pile.up.length - 1];
            if (onto ? canStack(card, onto) : canFillEmptyColumn(card, state.rules))
                return false;
        }
    }
    return true;
}
/** The suit-keyed empty foundations a fresh game starts with. */
export function emptyFoundations() {
    return { S: [], H: [], D: [], C: [] };
}
/** "a black Nine" — what a given card is waiting for underneath it. */
function wanted(onto) {
    const needed = RANKS[rankIndex(onto) - 1];
    if (!needed)
        return 'nothing — an Ace is the end of the line here';
    return `a ${isRed(onto.suit) ? 'black' : 'red'} ${RANK_NAMES[needed]}`;
}
/** Why `cards` will not go onto `to`. */
export function describeRefusal(ctx, cards, to) {
    const lead = cards[0];
    if (!lead)
        return 'There is nothing there to move.';
    if (to.zone === 'foundation') {
        if (cards.length > 1)
            return 'Foundations take one card at a time.';
        if (lead.suit !== to.suit) {
            return `${cardLabel(lead)} is not a ${SUIT_NAMES[to.suit].toLowerCase().replace(/s$/, '')}.`;
        }
        const top = ctx.foundations.find((f) => f.suit === to.suit)?.top ?? null;
        if (!top)
            return `The ${SUIT_NAMES[to.suit]} foundation starts with the Ace.`;
        const needed = RANKS[rankIndex(top) + 1];
        if (!needed)
            return `The ${SUIT_NAMES[to.suit]} foundation is already home.`;
        return `${SUIT_NAMES[to.suit]} is up to the ${RANK_NAMES[top.rank]} — the ${RANK_NAMES[needed]} is next.`;
    }
    const column = ctx.tableau[to.index];
    if (!column)
        return 'That column is not there.';
    const onto = column.up[column.up.length - 1];
    if (!onto) {
        if (column.downCount > 0)
            return 'Turn that column over before building on it.';
        return ctx.rules.anyCardFillsEmptyColumn
            ? 'That card cannot open a column.'
            : 'Only a King can go into an empty column.';
    }
    if (rankIndex(lead) !== rankIndex(onto) - 1) {
        return `${cardLabel(onto)} takes ${wanted(onto)}.`;
    }
    // Right rank, so the colour is what is wrong.
    return `${cardLabel(lead)} is the wrong colour — ${cardLabel(onto)} takes ${wanted(onto)}.`;
}
/** Why `cards` cannot be picked up at all, wherever they were headed. */
export function describeUnliftable(cards) {
    const lead = cards[0];
    if (!lead)
        return 'There is nothing there to move.';
    if (cards.length > 1 && !isValidRun(cards)) {
        return 'Only a run in sequence and alternating colours moves as one.';
    }
    return `Nothing will take the ${cardName(lead)} yet.`;
}
//# sourceMappingURL=legal.js.map